import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import api from '../api'

// Types
export interface AdminUser {
  id: string
  name: string
  email: string
  avatar?: string
  role: 'user' | 'admin' | 'super_admin'
  active: boolean
  companyId?: string | null
  company?: { id: string; name: string } | null
  createdAt: string
  updatedAt: string
  _count: {
    course_enrollments: number
  }
}

export interface UserEnrollment {
  id: string
  courseId: number
  enrolledAt: string
  enrolledBy: string | null
  startedAt: string | null
  completedAt: string | null
  course: {
    id: number
    title: string
    type: string
    accessType: string
    thumbnail?: string
  }
}

export interface AvailableCourse {
  id: number
  title: string
  description: string
  type: 'regular' | 'masterclass' | 'workshop' | 'trail'
  level: 'beginner' | 'intermediate' | 'advanced' | 'expert'
  accessType: 'free' | 'restricted' | 'paid'
  thumbnail?: string
}

export interface UpdateUserStatusDTO {
  active: boolean
}

export interface EnrollUserInCourseDTO {
  userId: string
  courseIds: number[]
}

// Hooks
export function useAdminUsers(search?: string, companyId?: string) {
  return useQuery({
    queryKey: ['admin-users', search, companyId],
    queryFn: async () => {
      const params = new URLSearchParams()
      if (search) params.set('search', search)
      if (companyId !== undefined) params.set('companyId', companyId)

      const response = await api.get<AdminUser[]>(`/admin/users?${params}`)
      return response.data
    },
  })
}

export function useUserEnrollments(userId: string) {
  return useQuery({
    queryKey: ['user-enrollments', userId],
    queryFn: async () => {
      const response = await api.get<UserEnrollment[]>(`/admin/users/${userId}/enrollments`)
      return response.data
    },
    enabled: !!userId,
  })
}

export function useAvailableCoursesForUser(userId: string) {
  return useQuery({
    queryKey: ['available-courses', userId],
    queryFn: async () => {
      const response = await api.get<AvailableCourse[]>(`/admin/users/${userId}/available-courses`)
      return response.data
    },
    enabled: !!userId,
  })
}

export function useUpdateUserStatus() {
  const queryClient = useQueryClient()

  return useMutation({
    mutationFn: async ({ userId, data }: { userId: string; data: UpdateUserStatusDTO }) => {
      const response = await api.patch<AdminUser>(`/admin/users/${userId}/status`, data)
      return response.data
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['admin-users'] })
    },
  })
}

export function useEnrollUserInCourses() {
  const queryClient = useQueryClient()

  return useMutation({
    mutationFn: async (data: EnrollUserInCourseDTO) => {
      // Para cada curso, fazer um POST /admin/courses/:id/enroll-user
      const promises = data.courseIds.map((courseId) =>
        api.post(`/admin/courses/${courseId}/enroll-user`, { userId: data.userId })
      )
      await Promise.all(promises)
      return { success: true }
    },
    onSuccess: (_, variables) => {
      queryClient.invalidateQueries({ queryKey: ['user-enrollments', variables.userId] })
      queryClient.invalidateQueries({ queryKey: ['available-courses', variables.userId] })
      queryClient.invalidateQueries({ queryKey: ['admin-users'] })
    },
  })
}

export function useUnenrollUserFromCourse() {
  const queryClient = useQueryClient()

  return useMutation({
    mutationFn: async ({ userId, courseId }: { userId: string; courseId: number }) => {
      await api.delete(`/admin/courses/${courseId}/unenroll-user/${userId}`)
      return { success: true }
    },
    onSuccess: (_, variables) => {
      queryClient.invalidateQueries({ queryKey: ['user-enrollments', variables.userId] })
      queryClient.invalidateQueries({ queryKey: ['available-courses', variables.userId] })
      queryClient.invalidateQueries({ queryKey: ['admin-users'] })
    },
  })
}

// Types for user activity details
export interface UserWatchedEpisode {
  episodeId: number
  episodeTitle: string
  moduleTitle: string
  completed: boolean
  watchTime: number
  lastWatchedAt: string
}

export interface UserWorkbookAction {
  id: number
  episodeId: number
  episodeTitle: string
  moduleTitle: string
  action: 'view' | 'download'
  workbookUrl: string
  createdAt: string
}

export interface UserCourseProgress {
  courseId: number
  courseTitle: string
  courseThumbnail?: string
  totalEpisodes: number
  completedEpisodes: number
  progressPercent: number
  enrolledAt: string
  lastActivityAt: string | null
}

export function useUserWatchedEpisodes(userId: string) {
  return useQuery({
    queryKey: ['admin-user-watched', userId],
    queryFn: async () => {
      const response = await api.get<UserWatchedEpisode[]>(
        `/admin/users/${userId}/watched-episodes`
      )
      return response.data
    },
    enabled: !!userId,
  })
}

export function useUserWorkbookActions(userId: string) {
  return useQuery({
    queryKey: ['admin-user-workbooks', userId],
    queryFn: async () => {
      const response = await api.get<UserWorkbookAction[]>(
        `/admin/users/${userId}/workbook-actions`
      )
      return response.data
    },
    enabled: !!userId,
  })
}

export function useUserCourseProgress(userId: string) {
  return useQuery({
    queryKey: ['admin-user-course-progress', userId],
    queryFn: async () => {
      const response = await api.get<UserCourseProgress[]>(`/admin/users/${userId}/course-progress`)
      return response.data
    },
    enabled: !!userId,
  })
}

export interface UserQuizAttempt {
  quizId: number
  quizTitle: string
  episodeTitle: string
  moduleTitle: string
  score: number
  correctAnswers: number
  totalQuestions: number
  completedAt: string
}

export function useUserQuizAttempts(userId: string) {
  return useQuery({
    queryKey: ['admin-user-quiz-attempts', userId],
    queryFn: async () => {
      const response = await api.get<UserQuizAttempt[]>(`/admin/users/${userId}/quiz-attempts`)
      return response.data
    },
    enabled: !!userId,
  })
}
